/**
 * Phase 14.15 Deliverable A — Phase-label sanitiser.
 *
 * Strips internal `phaseN` / `phaseN stepM` / `pN_stepM_` prefixes
 * from operator-visible campaign / scenario names BEFORE display.
 * Display-layer only — DB records remain untouched.
 *
 * The fall-back string `"(unnamed campaign)"` is used when the
 * entire input was a phase tag and nothing meaningful is left.
 *
 * Patterns matched (case-insensitive, multi-occurrence):
 *   • `phaseN stepM ` / `phaseN_stepM_` (most specific, must run first)
 *   • `pN_stepM_` / `pN stepM `
 *   • `phaseN ` / `phaseN_`  (least specific, runs last)
 *
 * Exported as a single function so import sites are tiny and
 * RTL-testable in isolation.
 */

const PHASE_PATTERNS = [
    // "phase 14 step 4 " / "phase14_step4_" / "phase14 step4_"
    /\bphase[\s_-]*\d+[\s_-]*step[\s_-]*\d+[\s_-]*/gi,
    // "p13_step4_" / "p13 step4 "
    /\bp\d+[\s_-]*step[\s_-]*\d+[\s_-]*/gi,
    // "phase11_" / "phase 14 " (must run AFTER step variants so the
    // step-suffix isn't left dangling)
    /\bphase[\s_-]*\d+[\s_-]*/gi,
];

const FALLBACK = "(unnamed campaign)";

export function scrubPhaseLabel(name) {
    if (typeof name !== "string" || !name.trim()) return FALLBACK;
    let out = name;
    for (const rx of PHASE_PATTERNS) {
        out = out.replace(rx, " ");
    }
    // Collapse stretches of separator chars introduced by the stripping.
    out = out
        .replace(/_{2,}/g, "_")
        .replace(/-{2,}/g, "-")
        .replace(/\s{2,}/g, " ");
    // Trim leading/trailing separators (whitespace, _, -).
    out = out.replace(/^[\s_-]+|[\s_-]+$/g, "");
    // Underscores → spaces for display readability (matches the PM
    // sample expectations: "phase11_contract_lifecycle" → "contract
    // lifecycle"). Preserve hyphens — they're often meaningful in
    // operator-supplied names ("Falcon-IX vs Shahed-136").
    out = out.replace(/_/g, " ");
    // Final whitespace collapse after underscore conversion.
    out = out.replace(/\s{2,}/g, " ").trim();
    if (!out) return FALLBACK;
    return out;
}
